import moment from 'moment';
import { useGetCriticalIncidentReferrals } from 'api/queries/criticalIncidentQuery';
import { TRAUMA_SERVICES } from 'components/ProviderProfile/constants';
import { useProviderActivitiesCount } from 'hooks/useProviderActivities';
import { useSessionFlag } from 'hooks/useSessionFlag';
import { useProviderContractedServices } from 'utils';
import { COUNTRY_CODE, useProviderCountryCode } from 'utils/regionUtils';
import { CIReferralStatus } from '../interfaces';

const REFERRAL_STALE_DAYS = 120;

export const useIsTraumaCounsellor = (providerId?: number) => {
    const { contractedServices, loading, error } = useProviderContractedServices(providerId ?? 0);
    const isAustralia = useProviderCountryCode(providerId) === COUNTRY_CODE.AUSTRALIA;
    const providerRole = useSessionFlag<'EAP' | 'CI' | 'MIXED' | 'NONE'>('provider-role');
    const { data: { activitiesCount = 0 } = {}, loading: activitiesCountLoading } = useProviderActivitiesCount(providerId);
    const { data: referrals, isFetching } = useGetCriticalIncidentReferrals('v1', !!providerId);
    const activeCasesCount = referrals?.filter(referral =>  referral.status === CIReferralStatus.Assigned && moment(referral.bookingStartTime).isSameOrAfter(moment().subtract(REFERRAL_STALE_DAYS, 'days'))).length || 0;
    const referralsCount = referrals?.filter(referral => (referral.status === CIReferralStatus.Open && moment(referral.bookingStartTime).isSameOrAfter(moment()) || referral.status === CIReferralStatus.Accepted)).length || 0;
    const hasRecentReferrals = referralsCount > 0 || activeCasesCount > 0;
    
    let isTraumaCounsellor = false;
    if (isAustralia) {
        isTraumaCounsellor = Boolean(
            contractedServices?.some((contractedService) => contractedService.service === 'Critical Incident Stress Management')
        );
    } else {
        isTraumaCounsellor = Boolean(
            contractedServices?.some((contractedService) => TRAUMA_SERVICES.includes(contractedService.service))
        );
    }
    isTraumaCounsellor = providerRole !== 'NONE' && (providerRole === 'CI' || providerRole === 'MIXED' || (!providerRole && isTraumaCounsellor));

    const isTraumaOnlyCounsellor = Boolean(
        (
            providerRole === 'CI' || (isAustralia
            ? contractedServices?.every(
                  (contractedService) => contractedService.service === 'Critical Incident Stress Management'
              )
            : contractedServices?.every((contractedService) => !!contractedService.service && TRAUMA_SERVICES.includes(contractedService.service)))
        ) && (providerRole === 'CI' || activitiesCount === 0)
    );
    const isEAPCounsellor = providerRole !== 'NONE' && !isTraumaOnlyCounsellor;
    const isEAPOnlyCounsellor = providerRole !== 'NONE' && (providerRole === 'EAP' || (!providerRole && !isTraumaCounsellor) && (providerRole === 'EAP' || !hasRecentReferrals));
    const isMixedCounsellor = providerRole !== 'NONE' && !isTraumaOnlyCounsellor && !isEAPOnlyCounsellor;

    return {
        isEAPCounsellor,
        isEAPOnlyCounsellor,
        isMixedCounsellor,
        isTraumaCounsellor,
        isTraumaOnlyCounsellor,
        loading: loading || (!referrals?.length && isFetching) || activitiesCountLoading,
        error
    };
};
